import React, { useMemo, useState } from 'react';
import { PackageSearch } from 'lucide-react';
import { Invoice, InvoiceLineItem } from 'src/redux/billing/billing.types';
import { useGetInvoiceLineItemValidationsQuery } from 'src/redux/billing/billing.endpoints';
import {
  countDistinctLineItemsWithMissingValidations,
  getMissingLineItemValidations,
} from './invoice-line-item-validation-utils';
import MissingLineItemValidationsModal from './missing-line-item-validations-modal';

interface MissingLineItemValidationsBannerProps {
  invoiceGroupId: string | undefined;
  invoices: Invoice[];
  orphanInvoiceLineItems?: InvoiceLineItem[];
  refetchInvoiceGroup: () => void | Promise<void>;
}

/**
 * Fetches validations for the invoice group, surfaces a banner when any row is missing,
 * and opens a detail modal on click.
 */
const MissingLineItemValidationsBanner: React.FC<MissingLineItemValidationsBannerProps> = ({
  invoiceGroupId,
  invoices,
  orphanInvoiceLineItems = [],
  refetchInvoiceGroup,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const { data: groupValidations = [], refetch: refetchGroupValidations } = useGetInvoiceLineItemValidationsQuery(
    { invoice_group: invoiceGroupId as string },
    { skip: invoiceGroupId == null || invoiceGroupId === '' }
  );

  const missingValidations = useMemo(
    () => getMissingLineItemValidations(groupValidations),
    [groupValidations]
  );

  const missingLineItemCount = useMemo(
    () => countDistinctLineItemsWithMissingValidations(groupValidations),
    [groupValidations]
  );

  if (invoiceGroupId == null || invoiceGroupId === '' || missingLineItemCount === 0) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className="flex w-full items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-left text-sm text-amber-950 transition-colors hover:bg-amber-100"
      >
        <PackageSearch className="h-4 w-4 shrink-0 text-amber-800" aria-hidden />
        <span>
          There may be
          {' '}
          <span className="font-semibold tabular-nums">
            {missingLineItemCount}
          </span>
          {' '}
          missing line item
          {missingLineItemCount === 1 ? '' : 's'}
          .
          {' '}
          <span className="text-amber-900 underline decoration-amber-700/60">
            View details
          </span>
        </span>
      </button>
      <MissingLineItemValidationsModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        validations={missingValidations}
        invoices={invoices}
        orphanInvoiceLineItems={orphanInvoiceLineItems}
        invoiceGroupId={invoiceGroupId}
        refetchValidations={refetchGroupValidations}
        refetchInvoiceGroup={refetchInvoiceGroup}
      />
    </>
  );
};

export default MissingLineItemValidationsBanner;
